import React, { useEffect, useState, useContext } from 'react';

import { useRouteMatch } from 'react-router-dom';
import { StudyContext } from 'packages/booking-contexts';
import { fetchAppsResource, fetchPiiResource } from 'packages/apis';
import { updateSfOpportunity } from 'packages/sales-force';
import { checkIfRequiredQuestionsComplete, getRedux } from 'packages/booking-components';
import { getPatientInformation } from './paymentFunctions';
import data from 'packages/booking-components/safety-questions/SafetyQuestions/SafetyQuestions.json';
import dataUK from 'packages/booking-components/safety-questions/SafetyQuestions/SafetyQuestions-v1.2.json';

const DEFAULT_PAYMENT_TYPE = 'stripe';

export const PaymentContext = React.createContext({});

export const PaymentContextProvider = ({ children }) => {
  const { studyId, selectedLocation, skuID, status, machineID } = useContext(StudyContext);
  const [paymentType, setPaymentType] = useState(DEFAULT_PAYMENT_TYPE);
  const [transactions, setTransactions] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({
    paid: 0,
    totalPrice: 0,
    discountPromo: 0,
    discountReferral: 0,
    serviceCreditUse: 0,
    referralCreditUse: 0,
    reconciliationAdjust: null,
    totalBalance: 0,
    availableCredits: 0,
  });
  const [total, setTotal] = useState(0);
  const [price, setPrice] = useState(0);

  const [viewTransaction, setViewTransaction] = useState(null);
  const [showSafetyQuestionsDialog, setShowSafetyQuestionsDialog] = useState(false);
  const [user, setUser] = useState({
    firstname: '',
    lastname: '',
    email: '',
    // eslint-disable-next-line camelcase
    user_id: '',
    roles: [],
  });

  const isOnAdminPayment = window.location.href.includes('admin/study');

  useEffect(() => {   
    console.log('invoiceSummary changed:', invoiceSummary);
    setTotal(Number(invoiceSummary.totalBalance).toFixed(2));
  }, [invoiceSummary]);

  useEffect(() => {
    if (selectedLocation?.type === 'whitelabel') {
      setPaymentType('invoice');
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (viewTransaction && !viewTransaction.issued && viewTransaction.paymentType === 'invoice') {
      setPaymentType(viewTransaction.paymentType);
    } else {
      setPaymentType(DEFAULT_PAYMENT_TYPE);
    }
  }, [viewTransaction]);

  const { path } = useRouteMatch();

  useEffect(() => {
    const isBooked = status?.booked;
    const showPrice = isBooked || path.includes('/booking');

    if (isBooked && invoiceSummary?.totalPrice) {
      setPrice(invoiceSummary.totalPrice);
      return;
    }

    if (!skuID || !selectedLocation?.catalog?.primary) {
      setPrice(0);
      setInvoiceSummary(prev => ({
        ...prev,
        totalPrice: 0,
        totalBalance: 0
      }));
      return;
    }

    const selectedSku = selectedLocation.catalog.primary.find((sku) => sku.skuID === skuID);
    if (!selectedSku) {
      setPrice(0);
      return;
    }

    const newPrice = showPrice ? selectedSku.price : 0;
    setPrice(newPrice);

    if (showPrice && studyId) {
      fetchAppsResource(`/payment/study/${studyId}/price`, 'POST', {
        skuID,
        price: newPrice,
      }).then(({ parsedBody: data }) => {
        if (data) {
          setInvoiceSummary(prev => ({
            ...prev,
            totalPrice: data.price,
            totalBalance: data.totalBalance,
          }));
          window.dispatchEvent(new CustomEvent('skuChanged', {
            detail: {
              studyId,
              skuID,
              newPrice: data.price,
              newTotalBalance: data.totalBalance,
              skuName: selectedSku.name
            }
          }));
        }
      }).catch(error => {
        console.error('Failed to update study price:', error);
      });
    }
  }, [skuID, selectedLocation, status, path, studyId]);

  const paymentArgChange = (change) => {
    if (change.paymentType) {
      setPaymentType(change.paymentType);
    }
    if (change.transactions) {
      setTransactions(change.transactions);
    }
    if (change.invoiceSummary) {
      setInvoiceSummary(prev => ({
        ...prev,
        ...change.invoiceSummary,
      }));
    }
  };

  const load = async (invoiceSummaryOnly = false) => {
    if (studyId) {
      const url = invoiceSummaryOnly
        ? `/payment/study/${studyId}/online-booking`
        : `/payment/study/${studyId}`;
      const { parsedBody: data } = await fetchAppsResource(url);
      setInvoiceSummary({
        ...invoiceSummary,
        totalPrice: data.totalPrice || 0,
        paid: data.paid || 0,
        discountPromo: data.discountPromo || 0,
        discountReferral: data.discountReferral || 0,
        discountReferralCredit: data.discountReferralCredit || 0,
        reconciliationAdjust: data.reconciliationAdjust,
        totalBalance: data.totalBalance || 0,
        availableCredits: data.availableCredits || 0,
      });
      if (data.transactions) {
        setTransactions(data.transactions);
      }
    }
  };

  const checkIfNeedSafetyQuestions = async (proactiveBooking, prenuvoId) => {
    if (!isOnAdminPayment && proactiveBooking) {
      const checkinState = await getRedux(studyId);
      const piiInformation = await getPatientInformation(prenuvoId);
      // Use UK safety questions if machineID indicates UK location
      const safetyQuestions = (machineID == null || machineID === '') ? [] : (machineID.includes('eng-') ? (dataUK?.subs || []) : (data?.subs || []));
      const requiredFields = checkIfRequiredQuestionsComplete({
        checkinContext: checkinState,
        piiContext: piiInformation,
        safetyQuestions,
      });
      if (requiredFields > 0) {
        setShowSafetyQuestionsDialog(proactiveBooking);
      }
    }
  };

  const recordPayment = async (paymentObj, callback) => {
    try {
      if (!studyId) {
        return 'Please Add Location and Time Before Completing a Payment';
      }
      const { parsedBody: data } = await fetchAppsResource(`/payment/study/${studyId}`, 'POST', {
        ...paymentObj,
        locationID: selectedLocation.locationID,
      });
      if (data?.redirect) {
        const { redirect } = data;
        if (typeof redirect === 'string') {
          window.location.href = redirect;
        }
      }

      if (callback && data?.paidStatus) {
        callback(data?.paidStatus, data?.paidTime);
      }

      if (data?.proactiveBooking) {
        const { parsedBody: study } = await fetchAppsResource(`/study/${studyId}`);
        if (selectedLocation.type !== 'whitelabel') {
          await updateSfOpportunity({
            study: {
              ...study,
              prenuvoId: study?.patient?.prenuvoID || study?.patient,
              stage: study.status.booked,
              appointmentTime: study.booking[0].utcStart,
            },
            newOpportunity: false,
          });
        }
        await checkIfNeedSafetyQuestions(
          data.proactiveBooking,
          study?.patient?.prenuvoID || study?.patient,
        );
      }

      return data;
    } catch ({ response: error, message }) {
      if (error?.data) {
        return error.data;
      }
      if (message) {
        return message;
      }
      return error;
    }
  };

  const updatePayment = async (paymentObj, callback) => {
    try {
      const { parsedBody: data } = await fetchAppsResource(
        `/payment/study/${studyId}/update/${paymentObj?.transactionID}`,
        'POST',
        paymentObj,
      );
      if (data) {
        const { redirect } = data;
        if (redirect) {
          window.location.href = redirect;
        }
      }

      if (callback && data?.paidStatus) {
        callback(data?.paidStatus, data?.paidTime);
      }
      checkIfNeedSafetyQuestions(data?.proactiveBooking, data?.prenuvoID);

      return data;
    } catch ({ response: error }) {
      if (error?.data) {
        return error.data;
      }
      return error;
    }
  };

  const refund = async (transactionID, refundDetails, onSuccess, onFail) => {
    try {
      const { parsedBody: data } = await fetchAppsResource(
        `/payment/study/${studyId}/refund/${transactionID}`,
        'POST',
        refundDetails,
      );
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      if (onFail) {
        onFail(err);
      }
    }
  };

  useEffect(() => {
    load(path === '/booking/:page/:studyId').then();
  }, [studyId]);

  const fetchCurrentUser = async () => {
    const { parsedBody } = await fetchPiiResource('/api/v1/userV2/current');
    const { user: userResponse } = parsedBody;

    setUser(userResponse);
  };

  useEffect(() => {
    fetchCurrentUser().then();
  }, [])

  return (
    <PaymentContext.Provider
      value={{
        user,
        studyId,
        paymentType,
        transactions,
        paymentArgChange,
        load,
        invoiceSummary,
        total,
        price,
        recordPayment,
        updatePayment,
        refund,
        viewTransaction,
        setViewTransaction,
        showSafetyQuestionsDialog,
        setShowSafetyQuestionsDialog
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};
